import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    Stack,
    TextField,
    Button,
    MenuItem,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableSortLabel,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import 'moment/locale/tr';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';

const chipStyles = {
    blue: { backgroundColor: '#F0F9FF', border: '1px solid #B9E6FE', color: '#026AA2', fontWeight: 500 },
    green: { backgroundColor: '#ECFDF3', border: '1px solid #ABEFC6', color: '#067647', fontWeight: 500 },
    red: { backgroundColor: '#FEF3F2', border: '1px solid #FECDCA', color: '#B42318', fontWeight: 500 },
    grey: { backgroundColor: '#F9FAFB', border: '1px solid #EAECF0', color: '#6B7280', fontWeight: 500 }
};

const RemainingTime = ({ endDate }) => {
    const [timeLeft, setTimeLeft] = useState('-');

    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = moment();
            const end = moment(endDate);
            const diff = end.diff(now);
            if (diff <= 0) {
                setTimeLeft('Süre Doldu');
                return;
            }

            const duration = moment.duration(diff);
            const hours = Math.floor(duration.asHours());
            const minutes = duration.minutes();

            setTimeLeft(`${hours} Saat ${minutes} Dakika`);
        };
        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 60000);
        return () => clearInterval(timer);
    }, [endDate]);

    return <>{timeLeft}</>;
};

export default function BoxOperationsPage() {
    // --- State'ler ---
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);

    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [transactions, setTransactions] = useState([]);
    const [locations, setLocations] = useState([]);

    const [searchText, setSearchText] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');

    const [startDate, setStartDate] = useState(moment());
    const [endDate, setEndDate] = useState(moment());

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('startDate');

    const [locationSearchText, setLocationSearchText] = useState('');

    // SMS Modal State'leri
    const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
    // Steps: 'SELECTION' | 'CONFIRM_EXISTING' | 'CONFIRM_NEW'
    const [smsDialogStep, setSmsDialogStep] = useState('SELECTION');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isSubmittingSms, setIsSubmittingSms] = useState(false);

    const [showCouponPayments, setShowCouponPayments] = useState(false);

    const [isStartDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [isEndDatePickerOpen, setEndDatePickerOpen] = useState(false);
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // --- RENDER SIRASINDA HESAPLAMA ---
    const isExpired = useMemo(() => {
        if (!selectedTransaction?.endDate) return false;
        return moment(selectedTransaction.endDate).isBefore(moment());
    }, [selectedTransaction]);

    // Lokasyonları Getir
    useEffect(() => {
        const fetchLocations = async () => {
            setLoading(true);
            try {
                const locationsResponse = await Services.GetDropboxLockerLocations();
                // Lokasyon verisinde de string parse gerekebilir, kontrol ediyoruz
                let locationData = locationsResponse.data.result;
                
                if (typeof locationData === 'string') {
                    try {
                        // Eğer tüm yapı string ise parse et
                        const parsed = JSON.parse(locationData);
                        // Eğer parse sonucu locations array'i dönüyorsa onu al, yoksa kendisini
                        locationData = parsed.locations || parsed; 
                    } catch (e) {
                        console.error("Location parse error", e);
                    }
                }

                if (Array.isArray(locationData)) {
                    const sortedLocations = locationData.sort((a, b) =>
                        a.name.localeCompare(b.name, 'tr')
                    );
                    setLocations(sortedLocations);
                } else {
                    setLocations([]);
                }
            } catch (err) {
                console.error("Failed to fetch locations:", err);
                setError('Lokasyonlar yüklenirken bir hata oluştu.');
                toast.error('Lokasyonlar yüklenirken bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        };

        fetchLocations();
    }, []);

    // --- GÜNCELLENEN FETCH TRANSACTIONS FONKSİYONU ---
    const fetchTransactions = async (locCode, sDate, eDate) => {
        if (!locCode) return;

        setLoading(true);
        setError(null);
        setPage(1);
        setTransactions([]); // Önceki veriyi temizle

        try {
            const formattedStartDate = moment(sDate).format('YYYY-MM-DD');
            const formattedEndDate = moment(eDate).add(1, 'days').format('YYYY-MM-DD');

            const response = await Services.GetDropboxAllTransactions(locCode, formattedStartDate, formattedEndDate);
            
            console.log("API Raw Response:", response.data); // Debug için

            // 1. ADIM: result verisini al
            let resultData = response.data.result;

            // 2. ADIM: String kontrolü ve Parse işlemi
            if (resultData && typeof resultData === 'string') {
                try {
                    resultData = JSON.parse(resultData);
                    console.log("Parsed Result:", resultData);
                } catch (e) {
                    console.error("JSON Parse Hatası:", e);
                    toast.error("Veri formatı hatalı.");
                    setLoading(false);
                    return;
                }
            }

            // 3. ADIM: Transactions dizisini ayıkla
            const transactionsArray = resultData && Array.isArray(resultData.transactions) 
                ? resultData.transactions 
                : [];

            setTransactions(transactionsArray);

            if (transactionsArray.length === 0) {
                toast.info("Seçili kriterlere uygun işlem bulunamadı.");
            }
        } catch (err) {
            console.error("Failed to filter transactions:", err);
            setError("İşlemler filtrelenirken bir hata oluştu.");
            toast.error("İşlemler filtrelenirken bir hata oluştu.");
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedLocation) {
            fetchTransactions(selectedLocation, startDate, endDate);
        }
    }, [selectedLocation]);

    const handleFilter = () => {
        if (!selectedLocation) {
            toast.warn("Lütfen bir konum seçiniz.");
            return;
        }
        if (!startDate || !endDate) {
            toast.warn("Lütfen tarih aralığı seçiniz.");
            return;
        }
        fetchTransactions(selectedLocation, startDate, endDate);
        toast.success("Tablo güncellendi.");
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filteredTransactions = useMemo(() => {
        if (!Array.isArray(transactions)) return [];

        let filtered = transactions.filter(op => {
            const matchesSearch = (op.nameSurName && op.nameSurName.toLowerCase().includes(searchText.toLowerCase())) ||
                (op.gsmNumber && op.gsmNumber.includes(searchText));

            const pType = String(op.paymentType);

            const matchesPaymentType = showCouponPayments
                ? (pType === '1' || pType === '3' || pType === '4' || pType === 'null')
                : (pType === '1' || pType === '4');

            return matchesSearch && matchesPaymentType;
        });

        if (orderBy === 'startDate') {
            filtered.sort((a, b) => {
                const dateA = new Date(a.startDate).getTime();
                const dateB = new Date(b.startDate).getTime();
                return order === 'asc' ? dateA - dateB : dateB - dateA;
            });
        }

        return filtered;
    }, [transactions, searchText, order, orderBy, showCouponPayments]);

    const pageCount = Math.ceil(filteredTransactions.length / rowsPerPage);
    const currentTableData = filteredTransactions.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    // --- SMS İŞLEMLERİ ---

    const handleGsmClick = (transaction) => {
        if (transaction.gsmNumber) {
            setSelectedTransaction(transaction);
            setSmsDialogStep('SELECTION'); 
            setIsSmsModalOpen(true);
        } else {
            toast.info("Bu işlem için telefon numarası bulunmuyor.");
        }
    };

    const handleProceedToExisting = () => {
        setSmsDialogStep('CONFIRM_EXISTING');
    };

    const handleProceedToNew = () => {
        setSmsDialogStep('CONFIRM_NEW');
    };

    const handleSendExistingSms = async () => {
        if (!selectedTransaction) return;

        setIsSubmittingSms(true);
        try {
            const boxNumber = selectedTransaction.lockerNumber || selectedTransaction.boxNumber || '';
            const message = `${boxNumber} Nolu dolaba ait şifreniz: ${selectedTransaction.otpCode}. Lütfen kimseyle paylaşmayınız.`;

            await Services.BoxOperationSendingSMS(selectedTransaction.gsmNumber, message);
            toast.success("Mevcut şifre başarıyla SMS olarak gönderildi.");
            setIsSmsModalOpen(false);
            setSelectedTransaction(null);
        } catch (error) {
            console.error("SMS gönderme hatası:", error);
            toast.error("SMS gönderilirken bir hata oluştu.");
        } finally {
            setIsSubmittingSms(false);
        }
    };

    const handleSendNewPassword = async () => {
        if (!selectedTransaction) return;

        setIsSubmittingSms(true);
        try {
            const response = await Services.MakeReloadBox(selectedTransaction.id); 
            
            const apiMessage = response.data?.message || response.data?.desc;

            if (response.status === 200) {
                toast.success(apiMessage || "İşlem başarıyla tamamlandı.");
            } else {
                toast.warning(apiMessage || "İşlem tamamlandı ancak bir uyarı var.");
            }

            setIsSmsModalOpen(false);
            setSelectedTransaction(null);

        } catch (error) {
            console.error("Reload işlemi hatası:", error);
            const errorMsg = error.response?.data?.message || error.response?.data?.desc || "İşlem sırasında bir hata oluştu.";
            toast.error(errorMsg);
        } finally {
            setIsSubmittingSms(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="tr">
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" fontWeight="bold">Dolap İşlemleri</Typography>
                </Stack>
                <Card>
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Dolap İşlemleri Listesi</Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                            <TextField size="small" placeholder="Kişi veya GSM No ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>), }} />
                            <TextField
                                select
                                label="Konum"
                                value={selectedLocation}
                                onChange={(e) => setSelectedLocation(e.target.value)}
                                size="small"
                                sx={{ minWidth: 240 }}
                            >
                                <Box onKeyDown={e => e.stopPropagation()} sx={{ p: 1, pt: 0, borderBottom: '1px solid #eee' }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Konum adı arayın..."
                                        value={locationSearchText}
                                        onChange={(e) => setLocationSearchText(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        InputProps={{
                                            startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>)
                                        }}
                                    />
                                </Box>
                                <MenuItem value="">Tüm Lokasyonlar</MenuItem>
                                {locations
                                    .filter(loc => loc.name.toLowerCase().includes(locationSearchText.toLowerCase()))
                                    .map((loc) => (
                                        <MenuItem key={loc.locationCode} value={loc.locationCode}>
                                            {loc.name}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                            <TextField
                                ref={startDateRef}
                                label="Başlangıç Tarihi"
                                size="small"
                                value={startDate ? moment(startDate).format('DD.MM.YYYY') : ''}
                                onClick={() => setStartDatePickerOpen(true)}
                                InputProps={{ readOnly: true, endAdornment: (<InputAdornment position="end"><CalendarTodayIcon color="action" /></InputAdornment>), }}
                            />
                            <TextField
                                ref={endDateRef}
                                label="Bitiş Tarihi"
                                size="small"
                                value={endDate ? moment(endDate).format('DD.MM.YYYY') : ''}
                                onClick={() => setEndDatePickerOpen(true)}
                                InputProps={{ readOnly: true, endAdornment: (<InputAdornment position="end"><CalendarTodayIcon color="action" /></InputAdornment>), }}
                            />
                            <Button onClick={handleFilter} variant="contained" sx={{ backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' }, px: 3 }}>
                                Filtrele
                            </Button>
                        </Stack>
                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showCouponPayments}
                                        onChange={(e) => setShowCouponPayments(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Admin İşlemlerini Göster"
                            />
                        </Box>
                    </Box>

                    <DatePicker open={isStartDatePickerOpen} onClose={() => setStartDatePickerOpen(false)} value={startDate} onChange={(newValue) => { setStartDate(newValue); setStartDatePickerOpen(false); }} slotProps={{ popper: { anchorEl: startDateRef.current }, textField: { sx: { display: 'none' } } }} />
                    <DatePicker open={isEndDatePickerOpen} onClose={() => setEndDatePickerOpen(false)} value={endDate} onChange={(newValue) => { setEndDate(newValue); setEndDatePickerOpen(false); }} slotProps={{ popper: { anchorEl: endDateRef.current }, textField: { sx: { display: 'none' } } }} />

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
                    ) : (
                        <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ mx: 2, maxWidth: 'calc(100% - 32px)' }}>
                            <Table sx={{ minWidth: 1640 }} aria-label="dolap işlemleri tablosu">
                                <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                                    <TableRow>
                                        <TableCell>İŞLEM NO</TableCell>
                                        <TableCell>KONUM</TableCell>
                                        <TableCell>DOLAP NO</TableCell>
                                        <TableCell sortDirection={orderBy === 'startDate' ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === 'startDate'}
                                                direction={orderBy === 'startDate' ? order : 'asc'}
                                                onClick={() => handleRequestSort('startDate')}
                                            >
                                                BAŞLANGIÇ TARİHİ
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>BİTİŞ TARİHİ</TableCell>
                                        <TableCell>ADI SOYADI</TableCell>
                                        <TableCell>GSM / E-MAIL</TableCell>
                                        <TableCell>İŞLEM TİPİ</TableCell>
                                        <TableCell>SÜRE</TableCell>
                                        <TableCell>ÖDEME TÜRÜ</TableCell>
                                        <TableCell>FİYAT</TableCell>
                                        <TableCell>DOLAP ŞİFRESİ</TableCell>
                                        <TableCell>DOĞRULAMA KODU</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentTableData.map((row) => (
                                        <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>{row.id || '-'}</TableCell>
                                            <TableCell>{row.locationCode || '-'}</TableCell>
                                            <TableCell><Chip label={row.lockerNumber} size="small" sx={chipStyles.grey} /></TableCell>
                                            <TableCell>{row.startDate ? moment(row.startDate).format('DD.MM.YYYY HH:mm') : '-'}</TableCell>
                                            <TableCell>{row.endDate ? moment(row.endDate).format('DD.MM.YYYY HH:mm') : '-'}</TableCell>
                                            <TableCell>{row.nameSurName || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.gsmNumber || row.email || '-'}
                                                    size="small"
                                                    sx={{
                                                        ...chipStyles.blue,
                                                        ...(row.gsmNumber && { cursor: 'pointer' })
                                                    }}
                                                    onClick={() => handleGsmClick(row)}
                                                />
                                            </TableCell>
                                            <TableCell>{row.orderType === 1 ? 'Yeni' : row.orderType === 2 ? ' Uzatma' : '-'}</TableCell>
                                            <TableCell><RemainingTime endDate={row.endDate} /></TableCell>
                                            <TableCell>{row.paymentType === '1' || row.paymentType === '4' ? 'Kredi Kartı' : row.paymentType === '3' ? 'Kupon' : '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.price !== null ? `${row.price} TL` : '-'}
                                                    size="small"
                                                    sx={chipStyles.green}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<LockOutlinedIcon sx={{ color: `${chipStyles.red.color} !important`, fontSize: '16px' }} />}
                                                    label={row.lockPass || row.otpCode || '****'}
                                                    size="small"
                                                    sx={chipStyles.red}
                                                />
                                            </TableCell>
                                            <TableCell><Chip label={row.verificationCode || '-'} size="small" sx={chipStyles.grey} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2">Sayfa başına satır:</Typography>
                            <TextField
                                select
                                size="small"
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                sx={{ '& .MuiInputBase-root': { borderRadius: '6px' }, width: '70px' }}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </TextField>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {`Toplam ${filteredTransactions.length} kayıttan ${filteredTransactions.length > 0 ? (page - 1) * rowsPerPage + 1 : 0}–${Math.min(page * rowsPerPage, filteredTransactions.length)} arası gösteriliyor.`}
                            </Typography>
                            <Pagination
                                count={pageCount}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                shape="rounded"
                            />
                        </Stack>
                    </Stack>
                </Card>

                <Dialog open={isSmsModalOpen} onClose={() => setIsSmsModalOpen(false)} PaperProps={{ sx: { borderRadius: '12px', width: 450 } }}>
                    <DialogTitle fontWeight={600}>SMS / Email Gönderme Onayı</DialogTitle>
                    <DialogContent>
                        {smsDialogStep === 'SELECTION' && (
                            <>
                                <Typography variant="body1" sx={{ mb: 3 }}>
                                    <b>{selectedTransaction?.nameSurName}</b> isimli kullanıcıya ait
                                    <b> {selectedTransaction?.gsmNumber}</b> numaralı telefonuna gönderilmek üzere işlem seçiniz.
                                </Typography>

                                <Stack spacing={2}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        sx={{ py: 1.5, borderColor: '#D0D5DD', color: '#344054', textTransform: 'none', fontWeight: 600 }}
                                        onClick={handleProceedToExisting}
                                    >
                                        Mevcut Şifreyi Gönder
                                    </Button>

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{ py: 1.5, backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' }, textTransform: 'none', fontWeight: 600 }}
                                        onClick={handleProceedToNew}
                                    >
                                        Yeni Şifre Gönder
                                    </Button>
                                </Stack>
                            </>
                        )}

                        {smsDialogStep === 'CONFIRM_EXISTING' && (
                            <>
                                <Typography>
                                    <b>{selectedTransaction?.nameSurName}</b> adlı kişiye ait
                                    <b> {selectedTransaction?.gsmNumber}</b> telefon numarasına,
                                    daha önce oluşturulmuş olan dolap şifresini tekrar göndermek üzeresiniz.
                                </Typography>
                                <Box sx={{ p: 2, mt: 2, backgroundColor: '#F9FAFB', borderRadius: '8px', textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">DOLAP ŞİFRESİ</Typography>
                                    <Typography variant="h5" fontWeight={700} letterSpacing={2}>
                                        {selectedTransaction?.otpCode || '****'}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {smsDialogStep === 'CONFIRM_NEW' && (
                            <>
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Dikkat: Bu işlem geri alınamaz.
                                </Alert>
                                <Typography>
                                    {isExpired
                                        ? "Süre dolduğu için, müşterinin şu anki işlemi ile yeni işlem birleşecektir. Müşterimize, 1 günlük yeni bir şifre gönderilecektir. Onaylıyor musunuz?"
                                        : "Müşterinin şu anki işlemi ile yeni işlem birleşecektir. Uzatma işlemi yapılarak, 1 günlük yeni bir şifre gönderilecektir. Onaylıyor musunuz?"
                                    }
                                </Typography>
                            </>
                        )}
                    </DialogContent>

                    {(smsDialogStep === 'CONFIRM_EXISTING' || smsDialogStep === 'CONFIRM_NEW') && (
                        <DialogActions sx={{ p: 3, pt: 1 }}>
                            <Button onClick={() => setIsSmsModalOpen(false)}>Vazgeç</Button>
                            <Button
                                onClick={smsDialogStep === 'CONFIRM_EXISTING' ? handleSendExistingSms : handleSendNewPassword}
                                variant="contained"
                                sx={{ backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' } }}
                                disabled={isSubmittingSms}
                            >
                                {isSubmittingSms ? <CircularProgress size={24} color="inherit" /> : 'Onayla ve Gönder'}
                            </Button>
                        </DialogActions>
                    )}
                </Dialog>
            </Container>
        </LocalizationProvider>
    );
}
